import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import ReportService from '@/services/reportService';
import { 
  getIssueLabel, 
  getPriorityLabel, 
  getStatusLabel, 
  getStatusColor, 
  getPriorityColor 
} from '@/utils/reportLabels';
import type { Marker, Report } from '@/types';

const MyReports: React.FC = () => {
  const navigate = useNavigate();
  const [markers, setMarkers] = useState<Marker[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);

  useEffect(() => {
    fetchUserReports();
  }, []);

  const fetchUserReports = async () => {
    try {
      setLoading(true);
      const data = await ReportService.getUserReports();
      setMarkers(data.markers);
    } catch (error: any) {
      console.error('Error fetching user reports:', error);
      toast.error(error.message || 'Errore nel caricamento delle segnalazioni');
    } finally {
      setLoading(false);
    }
  };

  const handleEditReport = (marker: Marker, report: Report) => {
    navigate(`/my-reports/edit/${marker._id}/${report._id}`, {
      state: { marker, report }
    });
  };

  const handleDeleteReport = async (markerId: string, reportId: string) => {
    if (!confirm('Sei sicuro di voler eliminare questa segnalazione?')) {
      return;
    }

    try {
      setDeleteLoading(reportId);
      await ReportService.deleteUserReport(markerId, reportId);

      // Remove the report from local state
      setMarkers(prev => prev.map(marker => {
        if (marker._id === markerId) {
          const updatedReports = marker.reports.filter(report => report._id !== reportId);
          return updatedReports.length > 0 
            ? { ...marker, reports: updatedReports }
            : null;
        }
        return marker;
      }).filter(Boolean) as Marker[]);

      toast.success('Segnalazione eliminata con successo');
    } catch (error: any) {
      console.error('Error deleting report:', error);
      toast.error(error.message || 'Errore nell\'eliminazione della segnalazione');
    } finally {
      setDeleteLoading(null);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const totalReports = markers.reduce((sum, marker) => sum + marker.reports.length, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Le Mie Segnalazioni
          </h1>
          <p className="text-gray-600">
            Gestisci le tue segnalazioni di problemi con la connessione
          </p>
        </div>

        <div className="mb-6 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {totalReports === 0 ? 'Nessuna segnalazione' : 
             totalReports === 1 ? '1 segnalazione trovata' : 
             `${totalReports} segnalazioni trovate`}
          </div>
          <Link
            to="/report"
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
          >
            + Nuova Segnalazione
          </Link>
        </div>

        {markers.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nessuna segnalazione trovata
            </h3>
            <p className="text-gray-600 mb-4">
              Non hai ancora creato nessuna segnalazione.
            </p>
            <Link
              to="/report"
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium transition-colors"
            >
              Crea la tua prima segnalazione
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {markers.map((marker) => (
              <div key={marker._id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {marker.streetName}
                  </h2>
                  {marker.streetNameCorrection && (
                    <p className="text-sm text-gray-600">
                      Nome corretto: {marker.streetNameCorrection}
                    </p>
                  )}
                  <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                    <span>
                      📍 {marker.coordinates.latitude.toFixed(6)}, {marker.coordinates.longitude.toFixed(6)}
                    </span>
                    <span>
                      📡 {marker.currentLine.technology}
                    </span>
                    {marker.currentLine.operator && (
                      <span>
                        🏢 {marker.currentLine.operator}
                      </span>
                    )}
                  </div>
                </div>

                <div className="p-6">
                  <div className="space-y-4">
                    {marker.reports.map((report) => (
                      <div key={report._id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-center space-x-3">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(report.status)}`}>
                              {getStatusLabel(report.status)}
                            </span>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(report.priority)}`}>
                              {getPriorityLabel(report.priority)}
                            </span>
                          </div>
                          <div className="text-sm text-gray-500">
                            {new Date(report.reportedAt).toLocaleDateString('it-IT')}
                          </div>
                        </div>

                        <div className="mb-3">
                          <p className="text-sm text-gray-600 mb-1">
                            <strong>Problema:</strong> {getIssueLabel(report.issue)}
                          </p>
                          {report.description && (
                            <p className="text-sm text-gray-700">{report.description}</p>
                          )}
                        </div>

                        {report.supportCount && report.supportCount > 1 && (
                          <div className="text-sm text-gray-500 mb-3">
                            👥 {report.supportCount} persone hanno segnalato questo problema
                          </div>
                        )}

                        {report.status === 'resolved' && report.resolutionNotes && (
                          <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-3">
                            <p className="text-sm text-green-800">
                              <strong>Risoluzione:</strong> {report.resolutionNotes}
                            </p>
                            {report.resolvedAt && (
                              <p className="text-xs text-green-600 mt-1">
                                Risolto il {new Date(report.resolvedAt).toLocaleDateString('it-IT')}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Actions - Only show for open reports */}
                        {(report.status === 'open' || report.status === 'in_progress') && (
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handleEditReport(marker, report)}
                              className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded transition-colors"
                            >
                              Modifica Tutto
                            </button>
                            <button
                              onClick={() => handleDeleteReport(marker._id, report._id)}
                              disabled={deleteLoading === report._id}
                              className="text-xs bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-3 py-1 rounded transition-colors flex items-center"
                            >
                              {deleteLoading === report._id ? (
                                <LoadingSpinner size="sm" className="mr-1" />
                              ) : null}
                              {deleteLoading === report._id ? 'Eliminando...' : 'Elimina'}
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
};

export default MyReports;